import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { ReactComponent as CloseIcon } from '../../../assets/svg/shared/icon-close.svg';
import { ReactComponent as IconCheckMark } from '../../../assets/svg/whiteCheck.svg';
import { IServerErrorResponse } from '../../../types';
import { useSetAccountTypeMutation } from '../../../hooks/mutations/useSetAccountTypeMutation';
import { useStore } from '../../../store';
import bugsnagHandler from '../../../services/bugsnag.service';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { SendMoneyContext } from '../pages/SendMoney';

const STYLES = {
  WRAPPER: 'flex w-[374px] h-[224px] rounded-[12px] bg-bw00 flex flex-col',
  HEADER: 'px-[24px] py-[20px] flex justify-between items-center border-b-[1px] border-bw03',
  TITLE: 'font-metricRMedium text-[18px] leading-4 text-bw08',
  CLOSE_ICON: 'cursor-pointer z-10 rounded-full fill-bw07 hover:bg-bw02',
  BODY: 'px-[24px]',
  ROW: 'h-[76px] flex justify-between items-center',
  ROW_TITLE: 'font-metricR text-md leading-6 text-bw08',
  ROW_VALUE:
    'rounded-[50%] w-[20px] h-[20px] rounded-full border-[1px] border-bw06 bg-bw00 flex items-center justify-center',
  ROW_VALUE_ACTIVE: 'bg-action03 border-none',
};

export const ACCOUNT_TYPES = {
  BUSINESS: 'BUSINESS',
  INDIVIDUAL: 'INDIVIDUAL',
};

interface IAccountTypeModalProps {
  linkedAccountId: string;
  onSuccess: () => void;
  onClose: () => void;
}

const AccountTypeModal = ({ onSuccess, linkedAccountId, onClose }: IAccountTypeModalProps) => {
  const { customerId } = useStore((state) => state.user);
  const { t } = useTranslation();
  const [chosenAccountType, setChosenAccountType] = useState<string>('');
  const { selectedLinkedAccount } = useContext(SendMoneyContext);
  const { mutateAsync: setAccountType, isLoading } = useSetAccountTypeMutation((error: IServerErrorResponse) => {
    bugsnagHandler.notify(
      `Failed to set account type for customerId: ${customerId} with the error: ${error?.response?.data?.errors?.[0]?.message || ''}`,
    );
    onSuccess();
  });
  useEffect(() => {
    if (chosenAccountType && !isLoading) {
      setAccountType({
        customerId,
        linkedAccountId,
        accountType: chosenAccountType,
      }).then(() => {
        onSuccess();
      });
      trackMixPanel('Did select plaid account type', {
        Flow: 'plaid transfer out',
        Type: chosenAccountType,
        institution_name: selectedLinkedAccount?.institutionName,
      });
    }
  }, [chosenAccountType]);
  const isAccountTypeBusiness = chosenAccountType === ACCOUNT_TYPES.BUSINESS;
  const isAccountTypeIndividual = chosenAccountType === ACCOUNT_TYPES.INDIVIDUAL;
  return (
    <UserMessage onClick={() => {}} id='AccountTypeModal'>
      <div className={STYLES.WRAPPER} data-testid='AccountTypeWrapper'>
        <div className={STYLES.HEADER}>
          <div className={STYLES.TITLE} data-testid='AccountTypeModal-title'>
            {t('ACCOUNT_TYPE_MODAL.TITLE')}
          </div>
          <CloseIcon className={STYLES.CLOSE_ICON} data-testid='AccountTypeModal-close' onClick={onClose} />
        </div>
        {isLoading && (
          <div className='absolute left-[50%] top-[50%] ml-[-10px] mt-[10px]'>
            <Spinner spinnerColor={SpinnerColor.Blue} height='20px' width='20px' />
          </div>
        )}
        <div className={`${STYLES.BODY} ${isLoading ? 'opacity-20' : ''}`}>
          <div
            className={STYLES.ROW}
            role='button'
            onClick={() => setChosenAccountType(ACCOUNT_TYPES.BUSINESS)}
            tabIndex={0}
            onKeyDown={() => {}}
            data-testid='AccountTypeModal-optionBusiness'
          >
            <div className={STYLES.ROW_TITLE}>{t('ACCOUNT_TYPE_MODAL.OPTION_BUSINESS')}</div>
            <div className={`${STYLES.ROW_VALUE} ${isAccountTypeBusiness ? STYLES.ROW_VALUE_ACTIVE : ''}`}>
              {isAccountTypeBusiness && <IconCheckMark className='w-[10px] h-[10px]' />}
            </div>
          </div>
          <div
            className={STYLES.ROW}
            role='button'
            onClick={() => setChosenAccountType(ACCOUNT_TYPES.INDIVIDUAL)}
            tabIndex={0}
            onKeyDown={() => {}}
            data-testid='AccountTypeModal-optionIndividual'
          >
            <div className={STYLES.ROW_TITLE}>{t('ACCOUNT_TYPE_MODAL.OPTION_INDIVIDUAL')}</div>
            <div className={`${STYLES.ROW_VALUE} ${isAccountTypeIndividual ? STYLES.ROW_VALUE_ACTIVE : ''}`}>
              {isAccountTypeIndividual && <IconCheckMark className='w-[10px] h-[10px]' />}
            </div>
          </div>
        </div>
      </div>
    </UserMessage>
  );
};

export default AccountTypeModal;
